import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search as SearchIcon, Info, Loader2, X } from "lucide-react";
import { API } from "@/lib/config";
import { useCountry } from "@/components/contexts/CountryContext";
import { usePlayer } from "@/components/contexts/PlayerContext";
import { CompactGrid } from "@/components/VideoCardCompact";

/**
 * Video search.
 *
 * The /api/search endpoint was fully built but had no route or UI - nothing in the
 * app could reach it. The query lives in the URL so a search is shareable and
 * survives refresh.
 */
export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { country } = useCountry();
  const { play } = usePlayer();

  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageToken, setPageToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searched, setSearched] = useState(false);
  // The backend answers most searches from its own index now, and says so when it
  // cannot reach YouTube at all. Dropping that message left an empty grid with no
  // explanation on the one day it matters.
  const [notice, setNotice] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => setInput(query), [query]);
  useEffect(() => inputRef.current?.focus(), []);

  useEffect(() => {
    if (!query.trim()) {
      setVideos([]);
      setSearched(false);
      setNotice(null);
      return;
    }
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/search`, {
          params: { q: query, country: country.code, max_results: 24 },
        });
        if (cancelled) return;
        setVideos(res.data.videos || []);
        setPageToken(res.data.next_page_token || null);
        setNotice(res.data.notice || null);
      } catch {
        if (!cancelled) {
          setVideos([]);
          setNotice("Search is unavailable right now. Try again in a moment.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSearched(true);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [query, country.code]);

  const loadMore = useCallback(async () => {
    if (!pageToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/search`, {
        params: { q: query, country: country.code, max_results: 24, page_token: pageToken },
      });
      setVideos((prev) => {
        const seen = new Set(prev.map((v) => v.id));
        return [...prev, ...(res.data.videos || []).filter((v) => !seen.has(v.id))];
      });
      setPageToken(res.data.next_page_token || null);
    } catch {
      setPageToken(null);
    } finally {
      setLoadingMore(false);
    }
  }, [pageToken, loadingMore, query, country.code]);

  const submit = (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    setSearchParams(trimmed ? { q: trimmed } : {}, { replace: true });
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="search-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="font-heading text-2xl sm:text-3xl font-black mb-6">Search</h1>

        <form onSubmit={submit} className="relative mb-8 max-w-2xl">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search videos, channels, topics..."
            className="w-full pl-11 pr-24 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none text-sm transition-colors"
            data-testid="search-input"
          />
          {input && (
            <button
              type="button"
              onClick={() => {
                setInput("");
                setSearchParams({}, { replace: true });
              }}
              className="absolute right-24 top-1/2 -translate-y-1/2 flex w-11 h-11 items-center justify-center text-white/40 hover:text-white"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-11 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 text-black text-xs font-bold transition-colors"
          >
            Search
          </button>
        </form>

        {notice && (
          <div
            className="mb-6 flex items-start gap-2 rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-xs text-orange-200"
            data-testid="search-notice"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{notice}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : (
          <>
            {searched && (
              <p className="text-xs text-white/40 mb-4">
                {videos.length > 0 ? `Results for "${query}"` : `Nothing found for "${query}"`}
              </p>
            )}
            <CompactGrid
              videos={videos}
              onPlay={play}
              emptyMessage={searched ? "No results. Try different keywords." : "Type something to start searching."}
            />
            {pageToken && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-5 py-2.5 rounded-xl border border-white/15 hover:bg-white/10 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
